import React from "react";
import {
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileText,
  Send,
  Ban,
  Circle,
  Shield,
  Loader,
  Hourglass,
  ArrowRight,
} from "lucide-react";
import { cn } from "@/lib/utils";

type BadgeVariant =
  | "gray"
  | "yellow"
  | "blue"
  | "green"
  | "red"
  | "purple"
  | "orange"
  | "emerald"
  | "amber"
  | "slate";

interface StatusBadgeProps {
  status: string;
  variant?: BadgeVariant;
  size?: "sm" | "md" | "lg";
  showIcon?: boolean;
  className?: string;
}

const normalize = (s: string) => String(s || "").toLowerCase().replace(/[\s-]+/g, "_");

const VARIANT_STYLES: Record<BadgeVariant, string> = {
  gray: "bg-gray-100 text-gray-700 border-gray-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  yellow: "bg-amber-50 text-amber-700 border-amber-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red: "bg-red-50 text-red-700 border-red-200",
  purple: "bg-purple-50 text-purple-700 border-purple-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  emerald: "bg-emerald-600 text-white border-emerald-700",
  amber: "bg-amber-100 text-amber-800 border-amber-200",
};

const STATUS_CONFIG: Record<
  string,
  { label: string; variant: BadgeVariant; icon?: React.ReactNode }
> = {
  // Loan statuses
  pending: { label: "Pending", variant: "yellow", icon: <Clock className="h-3 w-3" /> },
  pending_approval: { label: "Pending Approval", variant: "amber", icon: <Clock className="h-3 w-3" /> },
  awaiting_approval: { label: "Awaiting Approval", variant: "amber", icon: <Hourglass className="h-3 w-3" /> },
  approved_by_leaders: { label: "Approved by Leaders", variant: "blue", icon: <Shield className="h-3 w-3" /> },
  approved: { label: "Approved", variant: "green", icon: <CheckCircle2 className="h-3 w-3" /> },
  ready_for_disbursement: { label: "Ready for Disbursement", variant: "blue", icon: <ArrowRight className="h-3 w-3" /> },
  disbursed: { label: "Disbursed", variant: "blue", icon: <Send className="h-3 w-3" /> },
  active: { label: "Active", variant: "green", icon: <CheckCircle2 className="h-3 w-3" /> },
  overdue: { label: "Overdue", variant: "red", icon: <AlertTriangle className="h-3 w-3" /> },
  completed: { label: "Completed", variant: "emerald", icon: <CheckCircle2 className="h-3 w-3" /> },
  fully_repaid: { label: "Fully Repaid", variant: "emerald", icon: <CheckCircle2 className="h-3 w-3" /> },
  rejected: { label: "Rejected", variant: "red", icon: <XCircle className="h-3 w-3" /> },
  defaulted: { label: "Defaulted", variant: "red", icon: <Ban className="h-3 w-3" /> },
  cancelled: { label: "Cancelled", variant: "gray", icon: <Ban className="h-3 w-3" /> },
  draft: { label: "Draft", variant: "slate", icon: <FileText className="h-3 w-3" /> },

  // Declaration/workflow statuses
  awaiting_declaration: { label: "Awaiting Declaration", variant: "gray", icon: <Hourglass className="h-3 w-3" /> },
  awaiting_submission: { label: "Awaiting Submission", variant: "gray", icon: <Hourglass className="h-3 w-3" /> },
  awaiting_payroll: { label: "Awaiting Payroll", variant: "orange", icon: <Clock className="h-3 w-3" /> },
  awaiting_verification: { label: "Awaiting Verification", variant: "yellow", icon: <Clock className="h-3 w-3" /> },
  declared: { label: "Declared", variant: "blue", icon: <FileText className="h-3 w-3" /> },
  ready_for_hr: { label: "Ready for HR", variant: "blue", icon: <Send className="h-3 w-3" /> },
  sent_to_hr: { label: "Sent to HR", variant: "purple", icon: <Send className="h-3 w-3" /> },
  deducted: { label: "Deducted", variant: "green", icon: <CheckCircle2 className="h-3 w-3" /> },
  verified: { label: "Verified", variant: "green", icon: <CheckCircle2 className="h-3 w-3" /> },
  partial: { label: "Partial", variant: "orange", icon: <AlertTriangle className="h-3 w-3" /> },
  compliant: { label: "Compliant", variant: "green", icon: <CheckCircle2 className="h-3 w-3" /> },
  non_compliant: { label: "Non-Compliant", variant: "red", icon: <XCircle className="h-3 w-3" /> },

  // Contribution statuses
  pending_verification: { label: "Awaiting Verification", variant: "yellow", icon: <Clock className="h-3 w-3" /> },
  verified_by_secretary: { label: "Secretary Verified", variant: "blue", icon: <CheckCircle2 className="h-3 w-3" /> },
  approved_by_president: { label: "President Approved", variant: "green", icon: <CheckCircle2 className="h-3 w-3" /> },
  confirmed_by_accountant: { label: "Accountant Confirmed", variant: "green", icon: <CheckCircle2 className="h-3 w-3" /> },
  submitted_to_hr: { label: "Submitted to HR", variant: "purple", icon: <Send className="h-3 w-3" /> },
  deducted_from_salary: { label: "Deducted from Salary", variant: "emerald", icon: <CheckCircle2 className="h-3 w-3" /> },

  // Payment statuses
  paid: { label: "Paid", variant: "green", icon: <CheckCircle2 className="h-3 w-3" /> },
  unpaid: { label: "Unpaid", variant: "red", icon: <XCircle className="h-3 w-3" /> },
  late: { label: "Late", variant: "red", icon: <AlertTriangle className="h-3 w-3" /> },
  waived: { label: "Waived", variant: "gray", icon: <Ban className="h-3 w-3" /> },

  // Membership
  suspended: { label: "Suspended", variant: "red", icon: <Ban className="h-3 w-3" /> },
};

function getStatusConfig(status: string) {
  const normalized = normalize(status);
  if (STATUS_CONFIG[normalized]) return STATUS_CONFIG[normalized];

  if (normalized.includes("approve")) return STATUS_CONFIG.approved;
  if (normalized.includes("reject")) return STATUS_CONFIG.rejected;
  if (normalized.includes("pending") || normalized.includes("await")) return STATUS_CONFIG.pending;
  if (normalized.includes("complet") || normalized.includes("repaid")) return STATUS_CONFIG.completed;
  if (normalized.includes("active") || normalized.includes("disburs")) return STATUS_CONFIG.active;
  if (normalized.includes("overdue") || normalized.includes("late")) return STATUS_CONFIG.overdue;

  return { label: status || "Unknown", variant: "gray" as BadgeVariant, icon: <Circle className="h-3 w-3" /> };
}

const SIZE_STYLES = {
  sm: "px-2 py-0.5 text-[10px] gap-0.5",
  md: "px-2.5 py-0.5 text-xs gap-1",
  lg: "px-3 py-1 text-sm gap-1.5",
};

export function StatusBadge({
  status,
  variant: overrideVariant,
  size = "md",
  showIcon = true,
  className,
}: StatusBadgeProps) {
  const config = getStatusConfig(status);
  const variant = overrideVariant || config.variant;
  const styles = VARIANT_STYLES[variant] || VARIANT_STYLES.gray;
  const sizeStyles = SIZE_STYLES[size];

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-medium border whitespace-nowrap",
        styles,
        sizeStyles,
        className
      )}
      role="status"
      aria-label={config.label}
    >
      {showIcon && config.icon}
      {config.label}
    </span>
  );
}

// Compound variant for approval progress (e.g., "1/3 Approved")
export function ApprovalProgressBadge({
  approved,
  total,
}: {
  approved: number;
  total: number;
}) {
  if (approved === 0) {
    return <StatusBadge status="pending" size="sm" />;
  }
  if (approved >= total) {
    return <StatusBadge status="approved" size="sm" />;
  }
  return (
    <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-[10px] font-medium gap-1">
      <Loader className="h-3 w-3 animate-spin" style={{ animationDuration: "3s" }} />
      {approved}/{total} Approved
    </span>
  );
}

// N/A badge for truly not-applicable fields
export function NABadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full bg-gray-50 text-gray-400 border border-gray-100 px-2 py-0.5 text-[10px] font-medium",
        className
      )}
    >
      N/A
    </span>
  );
}

export default StatusBadge;
